import { motion } from 'framer-motion';
import { useInView } from 'framer-motion';
import { useRef } from 'react';
import { AlertCircle, Clock, Users, Shuffle, BarChart3, MessageSquareX } from 'lucide-react';

const problems = [
  {
    icon: MessageSquareX,
    title: 'Demandes mal gérées',
    description: 'Vous recevez des demandes de partout et certaines passent à la trappe.',
  },
  {
    icon: Clock,
    title: 'Manque de suivi',
    description: 'Impossible de savoir où en sont vos prospects dans le parcours.',
  },
  {
    icon: Users,
    title: 'Prospects perdus',
    description: 'Des opportunités qui s\'évaporent faute de relance au bon moment.',
  },
  {
    icon: Shuffle,
    title: 'Outils éparpillés',
    description: 'Plusieurs outils qui ne communiquent pas entre eux.',
  },
  {
    icon: BarChart3,
    title: 'Pas de visibilité',
    description: 'Difficile de mesurer ce qui fonctionne vraiment.',
  },
  {
    icon: AlertCircle,
    title: 'Temps gaspillé',
    description: 'Trop de tâches répétitives qui consomment votre énergie.',
  },
];

export const ProblemsSection = () => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: '-100px' });

  return (
    <section ref={ref} className="relative py-24 overflow-hidden">
      <div className="absolute inset-0 bg-gradient-subtle" />
      <div className="absolute inset-0 bg-dots-pattern opacity-20" />

      <div className="section-container relative z-10">
        {/* Section Header */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.6 }}
          className="text-center mb-16"
        >
          <span className="section-label mb-4 inline-block">
            Le problème
          </span>
          <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl font-bold text-foreground mb-6">
            Vous vous reconnaissez dans{' '}
            <span className="gradient-text">ces situations</span> ?
          </h2>
          <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
            Ces problèmes sont courants chez les TPE/PME. La bonne nouvelle : 
            ils peuvent être résolus avec les bons systèmes.
          </p>
        </motion.div>

        {/* Problems Grid */}
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
          {problems.map((problem, index) => (
            <motion.div
              key={problem.title}
              initial={{ opacity: 0, y: 30 }}
              animate={isInView ? { opacity: 1, y: 0 } : {}}
              transition={{ duration: 0.5, delay: index * 0.1 }}
              data-ui-sound-hover
              className="group relative p-6 rounded-2xl card-gradient card-hover"
            >
              {/* Icon */}
              <div className="w-12 h-12 rounded-xl bg-destructive/10 flex items-center justify-center mb-4 group-hover:bg-destructive/20 transition-colors">
                <problem.icon className="w-6 h-6 text-destructive" />
              </div>

              {/* Content */}
              <h3 className="font-display text-lg font-semibold text-foreground mb-2">
                {problem.title}
              </h3>
              <p className="text-sm text-muted-foreground">
                {problem.description}
              </p>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
};
